import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Link, Spinner } from '@amzn/awsui-components-react';
import {
  getIvtClient,
  GetUcFullCommand,
  type UcAssetLink,
  type UcAsset,
  type UcNode,
} from '../../../client/IvtHubClient';
import './AssetUcArsenal.css';

/**
 * AssetUcArsenal — the "Related" block on the Asset Detail page (Figma re-design):
 * 关联 UC 库存弹药 — {industry}: {UC}. For the UC this asset is matched to, list
 * ALL assets usable under that UC, split into IVT Assets (origin
 * ivt_legacy/ivt_incubated) and Partner & Global (partner/global), + the UC's
 * SFDC tag + the per-industry Highspot deep-link.
 *
 * Figma-aligned rewrite of NewAssetsPage/NewAssetUcArsenal (which stays on the
 * about-to-be-deprecated old page, unchanged): Cloudscape Link / Badge / Icon
 * instead of hand-rolled <a> + emoji, a file icon + row divider per asset, asset
 * name as a Link to /ivt/assets/:assetId, and the resource links (ARD/UAT/BOM/
 * Highspot) as external Links. Data fetch (GetUcFull scoped to industry, match
 * the UcNode by solutionMapId PK) is carried over verbatim — that part was right.
 */

/** Per-industry AWS Highspot home pages — the "Industry Highspot" deep link.
 *  Carried over from the prototype (GetUcFull carries no industry-level home). */
const UC_INDUSTRY_HOMES: Record<string, string> = {
  AUTO: 'https://aws.highspot.com/items/65a7a856c168ba2c5e7a1757',
  FSI: 'https://aws.highspot.com/items/65a7a91dec0d2b27d583c41e',
  MFG: 'https://aws.highspot.com/items/65a4bd971a4bc96cfc38b2e9',
  MEAD: 'https://aws.highspot.com/items/65acd866484867f2cb640fed',
  RCH: 'https://aws.highspot.com/items/6950bd1a390bf2a11c869e78',
  HCLS: 'https://aws.highspot.com/items/65a4ad13cc9e2350f1cb8169',
  GAME: 'https://aws.highspot.com/items/697194fabd31430ef609c2c5',
};

interface AssetUcArsenalProps {
  /** The selected asset's solutionMapLinks (from GetAsset → AssetDetail). */
  links: UcAssetLink[];
  /**
   * Bumped by the page after a header save, to force the GetUcFull refetch below.
   *
   * Needed because the effect keys off `industry` / `solutionMapId` — two primitives
   * that a rename does NOT change — while the rows it renders show each asset's
   * `name` (server side: COALESCE(a.name, a.use_case, a.id)). The selected asset is
   * itself one of those rows, so without this the page would show the new name in the
   * title and the OLD name in this card, which reads as "the save failed".
   */
  refreshKey?: number;
}

export default function AssetUcArsenal({ links, refreshKey = 0 }: AssetUcArsenalProps) {
  // The asset's UC = PRIMARY link, else the first link (single matched_uc).
  const primary = links.find((l) => l.role === 'PRIMARY') ?? links[0] ?? null;
  const industry = primary?.industry ?? null;
  // solutionMapId is the solution_map.id PK we match the UcNode by (unambiguous —
  // UC names collide across solution areas; the PK does not).
  const solutionMapId = primary?.solutionMapId ?? null;

  const [node, setNode] = useState<UcNode | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!industry || solutionMapId == null) {
      setNode(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const out = await getIvtClient().send(new GetUcFullCommand({ industry }));
        if (cancelled) return;
        const found = (out.industries ?? [])
          .flatMap((ind) => ind.areas ?? [])
          .flatMap((area) => area.ucs ?? [])
          .find((uc) => uc.solutionMapId === solutionMapId);
        setNode(found ?? null);
      } catch (e) {
        if (cancelled) return;
        setError((e as Error).message ?? 'Unknown error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [industry, solutionMapId, refreshKey]);

  // Topic-anchored / unlinked asset → no UC arsenal at all.
  if (!primary || !industry || solutionMapId == null) return null;

  const useCase = node?.name ?? primary.useCase;
  const arsenal = node?.arsenal ?? [];
  const isIvt = (a: UcAsset) => a.origin === 'ivt_legacy' || a.origin === 'ivt_incubated';
  const ivtItems = arsenal.filter(isIvt);
  const partnerItems = arsenal.filter((a) => !isIvt(a));
  const tag = node?.tag ?? '';
  const industryHome = UC_INDUSTRY_HOMES[industry] ?? '';

  return (
    <div className="ivt-arsenal">
      <div className="ivt-arsenal__header">
        <span className="ivt-arsenal__title">
          关联 UC 库存弹药 — {industry}: {useCase}
        </span>
        {industryHome && (
          <Link href={industryHome} external target="_blank">
            Industry Highspot
          </Link>
        )}
      </div>

      {loading && (
        <div className="ivt-arsenal__hint">
          <Spinner size="normal" /> 加载中…
        </div>
      )}

      {!loading && error && (
        <div className="ivt-arsenal__hint ivt-arsenal__hint--error">无法加载 UC 弹药库：{error}</div>
      )}

      {!loading && !error && arsenal.length === 0 && !tag && (
        <div className="ivt-arsenal__hint">该 UC 暂无弹药库数据</div>
      )}

      {!loading && !error && (arsenal.length > 0 || tag) && (
        <>
          {tag && (
            <div className="ivt-arsenal__tag-row">
              <span className="ivt-arsenal__tag-label">SFDC Tag:</span>
              <span className="ivt-arsenal__tag-pill">{tag}</span>
            </div>
          )}

          {ivtItems.length > 0 && (
            <>
              <div className="ivt-arsenal__group-label">{ivtItems.length} IVT Assets</div>
              {ivtItems.map((a) => (
                <AssetRow key={a.assetId ?? a.name} asset={a} />
              ))}
            </>
          )}

          {partnerItems.length > 0 && (
            <>
              <div className="ivt-arsenal__group-label">{partnerItems.length} Partner &amp; Global Assets</div>
              {partnerItems.map((p) => (
                <AssetRow key={p.assetId ?? p.name} asset={p} />
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
}

/** One arsenal asset row (Figma: file icon + name link + GenAI/year badges +
 *  SFDC Tag pill + one-line description, with resource links on the right). */
function AssetRow({ asset: a }: { asset: UcAsset }) {
  const navigate = useNavigate();
  return (
    <div className="ivt-arsenal__row">
      <div className="ivt-arsenal__row-main">
        <div className="ivt-arsenal__row-head">
          <span className="ivt-arsenal__file-icon">
            <Icon name="file" />
          </span>
          {a.assetId ? (
            <Link
              variant="primary"
              fontSize="body-m"
              onFollow={() => navigate(`/ivt/assets/${a.assetId}`)}
            >
              {a.name}
            </Link>
          ) : (
            <span className="ivt-arsenal__asset-name">{a.name}</span>
          )}
          {a.genai && <span className="ivt-arsenal__badge">GenAI</span>}
          {a.china && <span className="ivt-arsenal__badge">CN</span>}
          {a.type && <span className="ivt-arsenal__badge">{a.type}</span>}
          {a.year != null && <span className="ivt-arsenal__badge">{a.year}</span>}
        </div>
        {/* Always show the SFDC Tag row (even with no tag) so a tag-less asset
            isn't just a lone title line — render a muted placeholder instead. */}
        <div className="ivt-arsenal__tag-row ivt-arsenal__tag-row--asset">
          <span className="ivt-arsenal__tag-label">SFDC Tag:</span>
          {a.sfdcTag ? (
            <span className="ivt-arsenal__tag-pill">{a.sfdcTag}</span>
          ) : (
            <span className="ivt-arsenal__tag-empty">无</span>
          )}
        </div>
        {a.description && <div className="ivt-arsenal__desc">{a.description}</div>}
      </div>

      <div className="ivt-arsenal__links">
        {a.ardUrl && <ResourceLink href={a.ardUrl}>ARD</ResourceLink>}
        {a.uatUrl && <ResourceLink href={a.uatUrl}>UAT</ResourceLink>}
        {a.bomUrl && <ResourceLink href={a.bomUrl}>BOM</ResourceLink>}
        {(a.highspotUrl || a.url) && (
          <ResourceLink href={(a.highspotUrl || a.url) as string}>Highspot</ResourceLink>
        )}
      </div>
    </div>
  );
}

function ResourceLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link href={href} external target="_blank" fontSize="body-m">
      {children}
    </Link>
  );
}
